/** Accessible number input. Wraps React Aria NumberField. */
import {
  NumberField as AriaNumberField,
  Input,
  Button,
  Label,
  Text,
  Group,
  type NumberFieldProps as AriaNumberFieldProps,
} from 'react-aria-components';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { cn } from '@/foundation/utils/cn';

interface NumberFieldProps extends AriaNumberFieldProps {
  label?: string;
  description?: string;
  errorMessage?: string;
  className?: string;
  inputClassName?: string;
  size?: 'sm' | 'md';
}

const sizeClasses = {
  sm: 'px-2 py-1 text-[length:var(--f-text-sm)] h-8',
  md: 'px-3 py-2 text-[length:var(--f-text-sm)] h-10',
} as const;

function NumberField({
  label,
  description,
  errorMessage,
  size = 'md',
  className,
  inputClassName,
  ...props
}: NumberFieldProps) {
  return (
    <AriaNumberField className={cn('flex flex-col gap-1.5', className)} {...props}>
      {label && (
        <Label className="text-[length:var(--f-text-sm)] font-medium text-[color:var(--f-fg)]">
          {label}
        </Label>
      )}
      <Group
        className={cn(
          'flex items-center border rounded-[var(--f-radius-lg)] overflow-hidden transition-all',
          'border-[color:var(--f-border)] bg-[color:var(--f-bg-raised)]',
          'focus-within:ring-2 focus-within:ring-[color:var(--f-accent)]/20 focus-within:border-[color:var(--f-accent)]'
        )}
      >
        <Input
          className={cn(
            'flex-1 min-w-0 bg-transparent text-[color:var(--f-fg)] focus:outline-none transition-all',
            sizeClasses[size],
            inputClassName
          )}
        />
        <div className="flex flex-col border-l border-[color:var(--f-border)]">
          <Button
            slot="increment"
            className="flex items-center justify-center w-7 h-1/2 bg-[color:var(--f-bg-sunken)] hover:bg-[color:var(--f-bg-canvas)] text-[color:var(--f-fg-muted)] hover:text-[color:var(--f-fg)] transition-colors cursor-pointer border-b border-[color:var(--f-border)]"
          >
            <ChevronUp size={12} />
          </Button>
          <Button
            slot="decrement"
            className="flex items-center justify-center w-7 h-1/2 bg-[color:var(--f-bg-sunken)] hover:bg-[color:var(--f-bg-canvas)] text-[color:var(--f-fg-muted)] hover:text-[color:var(--f-fg)] transition-colors cursor-pointer"
          >
            <ChevronDown size={12} />
          </Button>
        </div>
      </Group>
      {description && (
        <Text
          slot="description"
          className="text-[length:var(--f-text-xs)] text-[color:var(--f-fg-muted)]"
        >
          {description}
        </Text>
      )}
      {errorMessage && (
        <Text
          slot="errorMessage"
          className="text-[length:var(--f-text-xs)] text-[color:var(--f-fg-danger)]"
        >
          {errorMessage}
        </Text>
      )}
    </AriaNumberField>
  );
}

export { NumberField };
export type { NumberFieldProps };
